import { useState, useEffect } from 'react';
import axios from 'axios';
import { fetchAuthSession } from '@aws-amplify/auth';
import Header from '../../components/general/Header.tsx';
import ErrorMessage from '../../components/ErrorMessage.js';
import LoadingMessage from '../../components/LoadingMessage.js';

const styles = `
  @import url('https://fonts.googleapis.com/css2?family=Bebas+Neue&family=DM+Sans:wght@400;500;600&display=swap');

  /* ── Light theme (default) ── */
  .ach-page {
    --ach-bg:               #f5f5f7;
    --ach-card-earned-bg:   rgba(240,192,64,0.15);
    --ach-card-earned-bg2:  rgba(240,192,64,0.05);
    --ach-card-earned-border: rgba(200,155,20,0.4);
    --ach-card-locked-bg:   rgba(0,0,0,0.03);
    --ach-card-locked-border: rgba(0,0,0,0.09);
    --ach-summary-bg:       rgba(0,0,0,0.04);
    --ach-summary-border:   rgba(0,0,0,0.09);
    --ach-summary-divider:  rgba(0,0,0,0.1);
    --ach-category-color:   rgba(0,0,0,0.4);
    --ach-title-color:      rgba(0,0,0,0.85);
    --ach-title-locked:     rgba(0,0,0,0.3);
    --ach-desc-color:       rgba(0,0,0,0.5);
    --ach-desc-locked:      rgba(0,0,0,0.2);
    --ach-date-color:       rgba(160,120,0,0.75);
    --ach-gold:             #c8880a;
    --ach-icon-locked:      grayscale(1) opacity(0.3);
    --ach-lock-bg:          rgba(0,0,0,0.06);
    --ach-lock-border:      rgba(0,0,0,0.12);
    --ach-glow:             rgba(240,192,64,0.15);
  }

  /* ── Dark theme ── */
  @media (prefers-color-scheme: dark) {
    .ach-page {
      --ach-bg:               #0f0f13;
      --ach-card-earned-bg:   rgba(240,192,64,0.12);
      --ach-card-earned-bg2:  rgba(240,192,64,0.04);
      --ach-card-earned-border: rgba(240,192,64,0.3);
      --ach-card-locked-bg:   rgba(255,255,255,0.03);
      --ach-card-locked-border: rgba(255,255,255,0.07);
      --ach-summary-bg:       rgba(255,255,255,0.04);
      --ach-summary-border:   rgba(255,255,255,0.08);
      --ach-summary-divider:  rgba(255,255,255,0.1);
      --ach-category-color:   rgba(255,255,255,0.35);
      --ach-title-color:      rgba(255,255,255,0.9);
      --ach-title-locked:     rgba(255,255,255,0.3);
      --ach-desc-color:       rgba(255,255,255,0.4);
      --ach-desc-locked:      rgba(255,255,255,0.18);
      --ach-date-color:       rgba(240,192,64,0.6);
      --ach-gold:             #f0c040;
      --ach-icon-locked:      grayscale(1) opacity(0.25);
      --ach-lock-bg:          rgba(255,255,255,0.08);
      --ach-lock-border:      rgba(255,255,255,0.15);
      --ach-glow:             rgba(240,192,64,0.2);
    }
  }

  .ach-page {
    padding: 10px 12px 40px;
    min-height: 100vh;
    background: var(--ach-bg);
  }

  .ach-summary {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 12px;
    margin: 16px 0 24px;
    padding: 14px 20px;
    background: var(--ach-summary-bg);
    border: 1px solid var(--ach-summary-border);
    border-radius: 12px;
  }

  .ach-summary-count {
    font-family: 'Bebas Neue', sans-serif;
    font-size: 2.8rem;
    line-height: 1;
    color: var(--ach-gold);
    letter-spacing: 1px;
  }

  .ach-summary-label {
    font-family: 'DM Sans', sans-serif;
    font-size: 0.85rem;
    color: var(--ach-category-color);
    text-transform: uppercase;
    letter-spacing: 0.08em;
    line-height: 1.3;
  }

  .ach-summary-divider {
    width: 1px;
    height: 40px;
    background: var(--ach-summary-divider);
  }

  .ach-category {
    margin-bottom: 28px;
  }

  .ach-category-title {
    font-family: 'Bebas Neue', sans-serif;
    font-size: 1.1rem;
    letter-spacing: 0.12em;
    color: var(--ach-category-color);
    text-transform: uppercase;
    margin: 0 0 10px 2px;
  }

  .ach-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
    gap: 10px;
  }

  .ach-card {
    position: relative;
    border-radius: 12px;
    padding: 16px 12px 14px;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 8px;
    text-align: center;
    transition: transform 0.15s ease, box-shadow 0.15s ease;
    cursor: default;
    overflow: hidden;
  }

  .ach-card.earned {
    background: linear-gradient(145deg, var(--ach-card-earned-bg), var(--ach-card-earned-bg2));
    border: 1px solid var(--ach-card-earned-border);
  }

  .ach-card.earned:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 24px var(--ach-glow);
  }

  .ach-card.locked {
    background: var(--ach-card-locked-bg);
    border: 1px solid var(--ach-card-locked-border);
  }

  .ach-icon-wrap {
    position: relative;
    width: 52px;
    height: 52px;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .ach-icon {
    font-size: 2rem;
    line-height: 1;
    transition: filter 0.2s;
  }

  .ach-card.locked .ach-icon {
    filter: var(--ach-icon-locked);
  }

  .ach-lock-icon {
    position: absolute;
    bottom: -2px;
    right: -2px;
    width: 18px;
    height: 18px;
    background: var(--ach-lock-bg);
    border: 1px solid var(--ach-lock-border);
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 0.6rem;
  }

  .ach-title {
    font-family: 'DM Sans', sans-serif;
    font-size: 0.78rem;
    font-weight: 600;
    line-height: 1.3;
    color: var(--ach-title-color);
  }

  .ach-card.locked .ach-title {
    color: var(--ach-title-locked);
  }

  .ach-desc {
    font-family: 'DM Sans', sans-serif;
    font-size: 0.7rem;
    color: var(--ach-desc-color);
    line-height: 1.4;
  }

  .ach-card.locked .ach-desc {
    color: var(--ach-desc-locked);
  }

  .ach-date {
    font-family: 'DM Sans', sans-serif;
    font-size: 0.65rem;
    color: var(--ach-date-color);
    margin-top: 2px;
  }

  .ach-earned-glow {
    position: absolute;
    top: -20px;
    right: -20px;
    width: 60px;
    height: 60px;
    background: radial-gradient(circle, var(--ach-glow) 0%, transparent 70%);
    pointer-events: none;
  }
`;

const CATEGORY_LABELS = {
  sessions: 'Sessions',
  skills: 'Skills',
  goals: 'Goals',
  routines: 'Routines',
  drills: 'Drills',
  conditioning: 'Conditioning',
  results: 'Results',
  combos: 'Combos',
  dmt: 'DMT',
  tumbling: 'Tumbling',
  play_skills: 'Play Skills',
  general: 'General',
};

const formatDate = (dateStr) => {
  if (!dateStr) return null;
  return new Date(dateStr).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
};

const Achievements = ({ userId }) => {
  const [achievements, setAchievements] = useState([]);
  const [isError, setIsError] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const session = await fetchAuthSession();
        const token = session.tokens.idToken;
        axios.defaults.headers['Authorization'] = `Bearer ${token}`;

        const response = await axios.get(`${process.env.REACT_APP_API_HOST}/api/achievements/${userId}`);
        setAchievements(response.data);
      } catch (err) {
        console.error('Error fetching achievements:', err);
        setIsError(true);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [userId]);

  if (isError) return <ErrorMessage handleConfirm={() => setIsError(false)} />;
  if (loading) return <LoadingMessage title="Achievements" />;

  const earned = achievements.filter(a => a.earned);
  const total = achievements.length;

  const grouped = achievements.reduce((acc, a) => {
    const cat = a.category || 'general';
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(a);
    return acc;
  }, {});

  Object.values(grouped).forEach(group => group.sort((a, b) => a.order - b.order));

  return (
    <>
      <style>{styles}</style>
      <Header title="Achievements" />

      <div className="ach-page">
        <div className="ach-summary">
          <div style={{ textAlign: 'center' }}>
            <div className="ach-summary-count">{earned.length}</div>
            <div className="ach-summary-label">Earned</div>
          </div>
          <div className="ach-summary-divider" />
          <div style={{ textAlign: 'center' }}>
            <div className="ach-summary-count">{total}</div>
            <div className="ach-summary-label">Total</div>
          </div>
          <div className="ach-summary-divider" />
          <div style={{ textAlign: 'center' }}>
            <div className="ach-summary-count">
              {total > 0 ? Math.round((earned.length / total) * 100) : 0}%
            </div>
            <div className="ach-summary-label">Complete</div>
          </div>
        </div>

        {Object.entries(grouped).map(([category, items]) => (
          <div key={category} className="ach-category">
            <div className="ach-category-title">
              {CATEGORY_LABELS[category] || category}
            </div>
            <div className="ach-grid">
              {items.map(achievement => (
                <AchievementCard key={achievement.id} achievement={achievement} />
              ))}
            </div>
          </div>
        ))}
      </div>
    </>
  );
};

const AchievementCard = ({ achievement }) => {
  const { earned, icon, title, description, achieved_on } = achievement;

  return (
    <div className={`ach-card ${earned ? 'earned' : 'locked'}`}>
      {earned && <div className="ach-earned-glow" />}
      <div className="ach-icon-wrap">
        <span className="ach-icon">{icon || '🏅'}</span>
        {!earned && <span className="ach-lock-icon">🔒</span>}
      </div>
      <div className="ach-title">{title}</div>
      {description && <div className="ach-desc">{description}</div>}
      {earned && achieved_on && (
        <div className="ach-date">{formatDate(achieved_on)}</div>
      )}
    </div>
  );
};

export default Achievements;